/**
 * Folder selector chip rendered inside the chat input toolbar.
 *
 * The folder for a chat is permanent once chosen, so the UI has three
 * shapes:
 *
 * 1. **Attached + healthy** — non-interactive chip showing the folder
 *    name. No dropdown, no swap, no detach.
 * 2. **Attached + needs reconnect** — single-action button that
 *    re-requests permission. Still locked to the same folder.
 * 3. **Not attached** — dropdown listing previously-used folders from
 *    the library plus an "Attach new folder…" item. The first pick
 *    binds the chat to that folder, after which states 1 / 2 take over.
 *
 * All storage logic lives in {@link useFolderAttachment} /
 * {@link folder-library}; this component only renders state.
 *
 * @module file-agent/components/folder-selector
 */

import {
  Check,
  ChevronDown,
  Folder,
  FolderOpen,
  FolderPlus,
  Link2,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { FolderAttachment } from "../lib/use-folder-attachment";

interface FolderSelectorProps {
  attachment: FolderAttachment;
  /** Disabled while the model is streaming. */
  disabled?: boolean;
  className?: string;
}

const CHIP_BASE = cn(
  "inline-flex h-8 max-w-[200px] shrink-0 items-center gap-1.5 rounded-md px-2 text-xs font-medium transition-colors",
  "disabled:cursor-not-allowed disabled:opacity-50",
);

export function FolderSelector({
  attachment,
  disabled,
  className,
}: FolderSelectorProps) {
  const { supported, handle, folderName, needsReconnect } = attachment;

  if (!supported) return null;

  if (handle && needsReconnect) {
    return (
      <ReconnectChip
        attachment={attachment}
        disabled={disabled}
        className={className}
      />
    );
  }

  if (handle) {
    return (
      <span
        className={cn(
          CHIP_BASE,
          "cursor-default text-foreground/90 bg-muted/40",
          className,
        )}
        title={`Folder: ${folderName ?? handle.name}`}
      >
        <FolderOpen className="size-3.5 shrink-0 text-primary" />
        <span className="truncate">{folderName ?? handle.name}</span>
        <span
          className="ml-0.5 size-1.5 shrink-0 rounded-full bg-success"
          aria-label="Connected"
        />
      </span>
    );
  }

  return (
    <UnattachedDropdown
      attachment={attachment}
      disabled={disabled}
      className={className}
    />
  );
}

/** Chip shown when permission has lapsed — single click re-grants. */
function ReconnectChip({
  attachment,
  disabled,
  className,
}: FolderSelectorProps) {
  const { folderName, reconnect } = attachment;

  const handleClick = async () => {
    try {
      const ok = await reconnect();
      if (ok) toast.success(`Reconnected to '${folderName}'`);
      else toast.error("Permission denied");
    } catch (err) {
      const message = (err as Error).message;
      if (!/aborted/i.test(message)) toast.error(message);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={cn(
        CHIP_BASE,
        "text-amber-700 hover:bg-amber-50 hover:text-amber-800 dark:text-amber-400 dark:hover:bg-amber-900/20",
        className,
      )}
      title={`Reconnect to '${folderName}'`}
    >
      <Link2 className="size-3.5 shrink-0" />
      <span className="truncate">Reconnect {folderName}</span>
    </button>
  );
}

/** Dropdown shown when no folder is attached yet. */
function UnattachedDropdown({
  attachment,
  disabled,
  className,
}: FolderSelectorProps) {
  const { library, attach, selectFolder, removeFolder } = attachment;

  const handleAttachNew = async () => {
    try {
      await attach();
      toast.success("Folder attached");
    } catch (err) {
      const message = (err as Error).message;
      if (!/aborted/i.test(message)) toast.error(`Could not attach: ${message}`);
    }
  };

  const handleSelect = async (id: string) => {
    try {
      await selectFolder(id);
    } catch (err) {
      const message = (err as Error).message;
      if (!/aborted/i.test(message)) toast.error(message);
    }
  };

  const handleRemove = async (id: string, name: string) => {
    try {
      await removeFolder(id);
      toast(`Removed '${name}' from library`);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            CHIP_BASE,
            "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
            className,
          )}
          title="Attach folder"
        >
          <Folder className="size-3.5 shrink-0" />
          <span className="truncate">Attach folder</span>
          <ChevronDown className="size-3 shrink-0 opacity-60" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-72">
        {library.length > 0 ? (
          <>
            <DropdownMenuLabel className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Recent folders
            </DropdownMenuLabel>
            {library.map((entry) => (
              <div
                key={entry.id}
                className="group/item relative flex items-center"
              >
                <DropdownMenuItem
                  onClick={() => handleSelect(entry.id)}
                  className="flex-1 gap-2 pr-9"
                >
                  <Folder className="size-3.5 shrink-0 text-muted-foreground" />
                  <span className="flex-1 truncate text-sm">{entry.name}</span>
                  <Check className="size-3.5 shrink-0 text-transparent" />
                </DropdownMenuItem>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(entry.id, entry.name);
                  }}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover/item:opacity-100 focus:opacity-100"
                  title={`Remove ${entry.name} from library`}
                  aria-label={`Remove ${entry.name}`}
                >
                  <Trash2 className="size-3" />
                </button>
              </div>
            ))}
            <DropdownMenuSeparator />
          </>
        ) : null}

        <DropdownMenuItem onClick={handleAttachNew} className="gap-2">
          <FolderPlus className="size-3.5 text-primary" />
          <span>Attach new folder…</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
